/**
 * Authentication Error Message Helper
 * 
 * Translates Firebase authentication error codes into user-friendly messages.
 * This improves the user experience by providing clear, actionable feedback
 * instead of technical error codes.
 * 
 * @module authErrorMessages
 */

/**
 * Maps Firebase auth error codes to user-friendly messages
 * 
 * @param {any} error - The error object from Firebase Auth
 * @returns {string} User-friendly error message
 */
export const getAuthErrorMessage = (error: any): string => {
    if (!error) {
        return 'An unexpected error occurred. Please try again.'
    }

    const errorCode = error?.code || ''
    
    // Map Firebase error codes to user-friendly messages
    switch (errorCode) {
        // Login errors
        case 'auth/invalid-login-credentials':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
            return 'Invalid email or password. Please check your credentials and try again.'
        
        case 'auth/invalid-email':
            return 'The email address is not valid. Please enter a valid email.'
        
        case 'auth/user-disabled':
            return 'This account has been disabled. Please contact your administrator.'
        
        case 'auth/too-many-requests':
            return 'Too many unsuccessful login attempts. Please try again later or reset your password.'
        
        // Sign up errors
        case 'auth/email-already-in-use':
            return 'An account with this email already exists. Please login instead or use a different email.'
        
        case 'auth/weak-password':
            return 'Password is too weak. Please use a stronger password with at least 8 characters, including uppercase, numbers, and special characters.'
        
        case 'auth/operation-not-allowed':
            return 'This sign-in method is not enabled. Please contact support.'
        
        // Network errors
        case 'auth/network-request-failed':
            return 'Network connection failed. Please check your internet connection and try again.'
        
        case 'auth/timeout':
            return 'The request timed out. Please check your internet connection and try again.'
        
        // Token errors
        case 'auth/invalid-credential':
            return 'Invalid credentials provided. Please try again.'
        
        case 'auth/credential-already-in-use':
            return 'This credential is already associated with a different account.'
        
        case 'auth/invalid-verification-code':
            return 'The verification code is invalid. Please try again.'
        
        case 'auth/invalid-verification-id':
            return 'The verification ID is invalid. Please try again.'
        
        // Session errors
        case 'auth/requires-recent-login':
            return 'This operation requires recent authentication. Please log in again.'
        
        case 'auth/expired-action-code':
            return 'This link has expired. Please request a new one.'
        
        case 'auth/invalid-action-code':
            return 'This link is invalid or has already been used.'
        
        // Multi-factor authentication errors
        case 'auth/multi-factor-auth-required':
            return 'Multi-factor authentication is required to complete sign-in.'
        
        // Account management errors
        case 'auth/account-exists-with-different-credential':
            return 'An account already exists with the same email but different sign-in credentials. Please use your original sign-in method.'
        
        case 'auth/popup-blocked':
            return 'The sign-in popup was blocked by your browser. Please allow popups for this site.'
        
        case 'auth/popup-closed-by-user':
            return 'The sign-in popup was closed before completing. Please try again.'
        
        case 'auth/cancelled-popup-request':
            return 'The sign-in request was cancelled. Please try again.'
        
        // Generic errors
        case 'auth/internal-error':
            return 'An internal error occurred. Please try again later.'
        
        case 'auth/invalid-api-key':
            return 'Configuration error. Please contact support.'
        
        case 'auth/app-not-authorized':
            return 'This app is not authorized to use Firebase Authentication. Please contact support.'
        
        // Default case - try to extract message from error
        default:
            // Check if there's a message property
            if (error?.message) {
                // Remove "Firebase: " prefix if present
                const message = error.message.replace(/^Firebase:\s*/i, '')
                // Remove error code in parentheses if present
                const cleanMessage = message.replace(/\s*\([^)]*\)\s*\.?$/, '')
                
                // If we got a clean message, use it
                if (cleanMessage && cleanMessage !== message) {
                    return cleanMessage + '.'
                }
                
                // If the message looks like a Firebase error code, make it friendly
                if (message.includes('auth/')) {
                    return 'Authentication failed. Please check your credentials and try again.'
                }
                
                return message
            }
            
            return 'An unexpected error occurred. Please try again.'
    }
}

/**
 * Checks if an error is related to invalid credentials
 * 
 * @param {any} error - The error object from Firebase Auth
 * @returns {boolean} True if error is related to invalid credentials
 */
export const isInvalidCredentialsError = (error: any): boolean => {
    if (!error) return false
    
    const errorCode = error?.code || ''
    return [
        'auth/invalid-login-credentials',
        'auth/wrong-password',
        'auth/user-not-found',
        'auth/invalid-email'
    ].includes(errorCode)
}

/**
 * Checks if an error is related to network issues
 * 
 * @param {any} error - The error object from Firebase Auth
 * @returns {boolean} True if error is related to network
 */
export const isNetworkError = (error: any): boolean => {
    if (!error) return false
    
    const errorCode = error?.code || ''
    return [
        'auth/network-request-failed',
        'auth/timeout'
    ].includes(errorCode)
}

/**
 * Checks if an error is related to too many attempts
 * 
 * @param {any} error - The error object from Firebase Auth
 * @returns {boolean} True if error is related to rate limiting
 */
export const isTooManyAttemptsError = (error: any): boolean => {
    if (!error) return false
    
    const errorCode = error?.code || ''
    return errorCode === 'auth/too-many-requests'
}

